import * as z from 'zod'
import { css } from '@emotion/react'
import { useTheme } from '@emotion/react'
import styled from '@emotion/styled'
import { zodResolver } from '@hookform/resolvers/zod'
import { m } from 'framer-motion'
import React from 'react'
import { useFieldArray, useForm } from 'react-hook-form'
import { useEffectOnce } from 'react-use'

import { isEmpty, noop } from '@livechat/data-utils'
import { useIsFocusWithin } from '@livechat/react-hooks'

import { usePreviewController } from '../WidgetPreview/previewController'
import Button from 'components/Button'
import ConfirmationModal from 'components/ConfirmationModal'
import EnhancementButtonLoader from 'components/EnhancementButtonLoader'
import FeatureToggle from 'components/FeatureToggle'
import { Portal } from 'components/Portal'
import { ToastKind } from 'components/Toast'
import UseAsyncFn from 'components/UseAsyncFn'
import Form from 'components/fields/Form'
import Input from 'components/fields/Input'
import Textarea from 'components/fields/Textarea'
import { PlusIcon, SparkleIcon, TrashIcon } from 'components/icons'
import { useFeature } from 'hooks/useFeature'
import { useIsOnboarding } from 'hooks/useIsOnboarding'
import { useTracking } from 'hooks/useTracking'
import { HeadingControlPortalContext } from 'layouts/DefaultLayout'
import { enhanceFAQAnswer, updateFeature, updateOrganization } from 'lib/api'
import { DEFAULT_FAQ } from 'lib/constants'
import { getOrdinalSuffix } from 'lib/helpers'
import { Store, useAppSelector, useAppStore } from 'lib/store'
import { EVENT_NAMES } from 'lib/tracking'
import trimWordsEnd from 'lib/trimWordsEnd'
import { FAQ, Question } from 'types/faq'
import { Feature } from 'types/feature'

const StyledForm = styled(Form)`
	padding-bottom: 60px;
`

const QuestionCard = styled.div`
	padding: ${({ theme }) => theme.spaces.space6};
	border-radius: ${({ theme }) => theme.borderRadiuses.xl};
	border: 1px solid ${({ theme }) => theme.colors.light.surfaceDecorative};
`

const QuestionIndex = styled.div`
	width: 40px;
	height: 24px;
	display: flex;
	align-items: center;
	justify-content: center;
	background-color: #e6e6fa;
	border-radius: ${({ theme }) => theme.borderRadiuses.xl};
	${({ theme }) => theme.typography.captionMedium};
`

const CardFooter = styled(m.div)<{ isOnboarding: boolean }>`
	position: absolute;
	bottom: 0;
	left: 0;
	right: 0;
	height: 50px;
	display: flex;
	padding: 0 ${({ theme }) => theme.spaces.space3};
	background: linear-gradient(180deg, rgba(255, 255, 255, 0) 0%, #fafafa 100%);

	${({ theme, isOnboarding }) =>
		!theme.isMobile &&
		isOnboarding &&
		css`
			height: 150px;
			width: 50%;
			padding-bottom: 100px;
			padding-right: 80px;
			flex-direction: row-reverse;
		`}
`

const AddQuestionButton = styled(Button)`
	padding: ${({ theme }) => theme.spaces.space3};
	margin-right: ${({ theme }) => theme.spaces.space6};
	margin-bottom: ${({ theme }) => theme.spaces.space6};
	box-shadow: ${({ theme }) => theme.boxShadows.floating};
	z-index: ${({ theme }) => theme.zIndexes.base};
	background-color: ${({ theme }) => theme.colors.light.surface};
	color: ${({ theme }) => theme.colors.light.primaryTextColor};
	border: 1px solid ${({ theme }) => theme.colors.light.primaryTextColor};
	flex-direction: row-reverse;
	padding-right: ${({ theme }) => theme.spaces.space5};
	${({ theme }) => theme.typography.captionMedium};
`

const FormSchema = z.object({
	questions: z.array(
		z.object({
			summary: z
				.string()
				.min(1, 'Question cannot be empty')
				.max(100, 'Cannot be longer than 100 characters')
				.regex(/.*\S.*/, 'Question cannot be empty'),
			details: z
				.string()
				.min(1, 'Answer cannot be empty')
				.max(500, 'Cannot be longer than 500 characters')
				.regex(/.*\S.*/, 'Answer cannot be empty'),
		}),
	),
})

type FormValues = z.infer<typeof FormSchema>

export function syncStoreWithQuestionsForm(store: Store, formValues: Partial<FormValues>, isEnabled: boolean) {
	store.dispatch({
		type: 'faq/set',
		payload: {
			enabled: isEnabled,
			properties: {
				questions: formValues.questions ?? DEFAULT_FAQ.questions,
			},
		},
	})
}

const FAQ_CARD_ITEMS_THRESHOLD = 3

function withDefaulValues(FormComponent: typeof QuestionsForm) {
	const WithDefaultValues = React.forwardRef<HTMLFormElement, Props>((props, ref) => {
		const { feature: faq, isFeatureLoading: isFaqLoading } = useFeature('faq')

		if (isFaqLoading) {
			return null
		}

		const parseDataToFormValues = (questions: FAQ['questions']): FormValues => ({ questions })

		return (
			<FormComponent
				{...props}
				ref={ref}
				defaultValues={parseDataToFormValues(faq?.properties.questions ?? DEFAULT_FAQ.questions)}
			/>
		)
	})
	WithDefaultValues.displayName = `WithDefaultValues(${FormComponent.displayName})`
	return WithDefaultValues
}

type Props = {
	id: string
	onSubmit?: VoidFunction
	onIsValidChanged?(isValid: boolean, id: string): void
	onHasErrorsChanged?(hasErrors: boolean, id: string): void
	onIsDirtyChanged?(isDirty: boolean, id: string): void
	onIsSubmittingChanged?(isSubmitting: boolean, id: string): void
	onSubmitResult?(result: ToastKind): void
	shouldShowAddQuestionButton?: boolean
}

const ConfirmEnableModal = ({
	onConfirmSave,
	onKeepEditing,
}: {
	onConfirmSave: () => void
	onKeepEditing: () => void
}) => {
	return (
		<ConfirmationModal
			name="ConfirmEnableModal"
			data-testid="ConfirmEnableModal"
			title="You have unsaved changes"
			caption="Do you want to save new changes and enable FAQ? "
			hideModal={onKeepEditing}
		>
			<Button kind="secondary" type="button" mediumDesktop onClick={onKeepEditing}>
				Keep editing
			</Button>
			<Button kind="primary" type="button" mediumDesktop onClick={onConfirmSave}>
				Save and enable
			</Button>
		</ConfirmationModal>
	)
}

const QuestionsForm = React.forwardRef<HTMLFormElement, Props & { defaultValues: FormValues }>(
	(
		{
			id,
			defaultValues,
			onIsValidChanged,
			onHasErrorsChanged,
			onIsDirtyChanged,
			onIsSubmittingChanged,
			onSubmit,
			onSubmitResult,
			shouldShowAddQuestionButton = true,
		},
		ref,
	) => {
		const store = useAppStore()
		const track = useTracking()
		const form = useForm<FormValues>({
			defaultValues,
			mode: 'all',
			resolver: zodResolver(FormSchema),
		})
		const { control, trigger, watch, register, reset, handleSubmit, getValues, setValue, formState } = form
		const { feature: faq, mutateFeature: mutateFaq } = useFeature('faq')
		const { fields, append, remove } = useFieldArray({ control, name: 'questions' })
		const { errors, isDirty, isValid, dirtyFields } = formState
		const [isFocusWithin, focusWithinProps] = useIsFocusWithin()
		const previewController = usePreviewController()
		const hasErrors = !isEmpty(errors)
		const [isSubmitting, setIsSubmitting] = React.useState(false)
		const displayConfirmationModal = () => store.dispatch({ type: 'currentModal/set', payload: 'ConfirmEnableModal' })
		const hideConfirmationModal = () => store.dispatch({ type: 'currentModal/set', payload: null })
		const theme = useTheme()
		const isOnboarding = useIsOnboarding()
		const newFaq = useAppSelector((state) => state.faq)
		const isEnabled = faq?.enabled ?? false
		const shouldShowForm = !isOnboarding || isEnabled

		const showInPreview = (expectedQuestionsCount: number) => {
			const hasFAQTab = expectedQuestionsCount > FAQ_CARD_ITEMS_THRESHOLD
			previewController.showView(hasFAQTab ? 'FAQ' : 'Homescreen', 'accordion-card')
		}

		useEffectOnce(() => showInPreview(fields.length))

		React.useEffect(() => {
			syncStoreWithQuestionsForm(store, getValues(), isEnabled)
			const { unsubscribe } = watch((formValues) => {
				syncStoreWithQuestionsForm(store, formValues as Partial<FormValues>, isEnabled)
			})
			return () => unsubscribe()
		}, [getValues, isEnabled, store, watch])

		React.useEffect(() => {
			if (!isFocusWithin) {
				trigger()
			}
		}, [isFocusWithin, trigger])

		React.useEffect(() => {
			onIsValidChanged?.(isValid, id)
		}, [id, onIsValidChanged, isValid])

		React.useEffect(() => {
			onHasErrorsChanged?.(hasErrors, id)
		}, [id, onHasErrorsChanged, hasErrors])

		React.useEffect(() => {
			onIsDirtyChanged?.(isDirty, id)
		}, [id, isDirty, onIsDirtyChanged])

		React.useEffect(() => {
			onIsSubmittingChanged?.(isSubmitting, id)
		}, [id, isSubmitting, onIsSubmittingChanged])

		const handleAddQuestion = () => {
			if (getValues('questions').length < 10) {
				append({ summary: '', details: '' })
				showInPreview(fields.length + 1)
			}
		}

		const handleRemoveQuestion = (index: number) => {
			if (getValues('questions').length > 1) {
				remove(index)
				showInPreview(fields.length - 1)
			}
		}

		const handleReset = (event: React.FormEvent) => {
			event.preventDefault()
			reset(defaultValues)
		}

		const trackSubmission = (newFaq: Feature<'faq'>['properties']) => {
			if (!newFaq.questions) {
				return
			}

			const questionsLengthDiff = defaultValues.questions.length - (newFaq.questions?.length ?? 0)

			track(EVENT_NAMES.FAQ_SAVED, {
				isEnabled,
				questionsCustomized: defaultValues.questions.some(({ summary, details }, questionIndex) => {
					const currentQuestion = newFaq.questions?.[questionIndex]
					if (!currentQuestion) {
						return false
					}
					return currentQuestion.summary !== summary || currentQuestion.details !== details
				}),
				questionsAdded: questionsLengthDiff < 0,
				questionsDeleted: questionsLengthDiff > 0,
				quantity: newFaq.questions?.length ?? 0,
			})
		}

		const submitForm = async (_isEnabled: boolean) => {
			setIsSubmitting(true)

			const { faq: newFaq } = store.getState()

			if (!newFaq || !newFaq.properties) {
				return
			}

			trackSubmission(newFaq.properties)

			try {
				const savedFAQ = await updateFeature('faq', {
					enabled: _isEnabled,
					...(!isEmpty(dirtyFields) && {
						properties: {
							...(dirtyFields.questions && { questions: newFaq.properties.questions }),
						},
					}),
				})

				mutateFaq(savedFAQ, false)

				if (isOnboarding) {
					await updateOrganization({ onboardingProgress: 'provide-self-help' })
				}
				reset({ questions: savedFAQ.properties.questions ?? defaultValues.questions })
				onSubmit?.()
				onSubmitResult?.('success')
			} catch (error) {
				onSubmitResult?.('error')
				throw error
			} finally {
				setIsSubmitting(false)
			}
		}

		const _onSubmit = async () => {
			submitForm(newFaq?.enabled ?? false)
		}

		const handleEnhanceAnswer = async (index: number) => {
			const { result } = await enhanceFAQAnswer(
				getValues(`questions.${index}.summary`),
				getValues(`questions.${index}.details`),
			)

			setValue(`questions.${index}.details`, trimWordsEnd(result, 500), { shouldTouch: true, shouldDirty: true })
		}

		const handleSwitchChange = async (_isEnabled: boolean): Promise<{ isEnabled: boolean }> => {
			try {
				const updatedFeature = await updateFeature('faq', { enabled: _isEnabled })

				mutateFaq(updatedFeature, { revalidate: false })
				syncStoreWithQuestionsForm(
					store,
					{ questions: updatedFeature.properties.questions as Question[] },
					updatedFeature.enabled,
				)
				onSubmitResult?.('success')
				return { isEnabled: _isEnabled }
			} catch {
				onSubmitResult?.('error')
				return { isEnabled: !_isEnabled }
			}
		}

		const handleOnboardingSwitchChange = async (_isEnabled: boolean) => {
			if (faq) {
				mutateFaq({ ...faq, enabled: _isEnabled }, { revalidate: false })
				syncStoreWithQuestionsForm(store, faq.properties, _isEnabled)
			}

			return { isEnabled: _isEnabled }
		}

		const handleToggleClick = (event: React.MouseEvent<HTMLDivElement>) => {
			if (isEnabled || !isDirty || !isValid) {
				return
			}

			event.preventDefault()
			event.stopPropagation()
			track(EVENT_NAMES.MODAL_DISPLAYED, { name: 'Confirm enable' })
			displayConfirmationModal()
		}

		const handleEnableWithCurrentFAQ = () => {
			submitForm(true)
			hideConfirmationModal()
			track(EVENT_NAMES.MODAL_CLOSED, { name: 'Confirm enable', action: 'Save and enable' })
		}

		const handleKeepEditing = () => {
			hideConfirmationModal()
			track(EVENT_NAMES.MODAL_CLOSED, { name: 'Confirm enable', action: 'Keep editing' })
		}

		return (
			<>
				<ConfirmEnableModal onKeepEditing={handleKeepEditing} onConfirmSave={handleEnableWithCurrentFAQ} />
				<StyledForm id={id} onSubmit={handleSubmit(_onSubmit)} onReset={handleReset} {...focusWithinProps} ref={ref}>
					<Portal context={HeadingControlPortalContext}>
						<div onClick={handleToggleClick}>
							<FeatureToggle
								isEnabled={isEnabled}
								onChange={handleSwitchChange}
								disabled={isSubmitting || (!isEnabled && !isValid)}
								ariaName="faq"
								trackingName="FAQ"
							/>
						</div>
					</Portal>
					{isOnboarding && (
						<FeatureToggle
							isEnabled={isEnabled}
							onChange={handleOnboardingSwitchChange}
							disabled={isSubmitting || (!isEnabled && !isValid)}
							ariaName="faq"
							trackingName="FAQ"
							label="Show FAQ"
						/>
					)}
					{shouldShowForm &&
						fields.map((field, index) => (
							<QuestionCard key={field.id}>
								<Input
									leftAdornment={<QuestionIndex>{getOrdinalSuffix(index + 1)}</QuestionIndex>}
									rightAdornment={
										fields.length > 1 && (
											<Button
												compact
												type="button"
												kind="text"
												onClick={() => handleRemoveQuestion(index)}
												aria-label="remove question"
											>
												<TrashIcon />
											</Button>
										)
									}
									label="Question"
									error={errors.questions?.[index]?.summary?.message}
									placeholder="Provide a question"
									{...register(`questions.${index}.summary`)}
								/>
								<UseAsyncFn key={`questions.${index}`} asyncFn={() => handleEnhanceAnswer(index).catch(noop)}>
									{({ loading, callAsyncFn }) => (
										<Textarea
											label="Answer"
											disabled={loading}
											error={errors.questions?.[index]?.details?.message}
											placeholder="Provide the answer"
											{...register(`questions.${index}.details`)}
											rightAdornment={
												<EnhancementButtonLoader isVisible={loading}>
													<Button
														compact
														type="button"
														kind="surface"
														loading={loading}
														disabled={loading}
														onClick={callAsyncFn}
														aria-label="enhance answer with AI"
													>
														<SparkleIcon />
													</Button>
												</EnhancementButtonLoader>
											}
										/>
									)}
								</UseAsyncFn>
							</QuestionCard>
						))}
				</StyledForm>
				<CardFooter
					initial={{ opacity: 0, y: 30 }}
					animate={{ opacity: 1, y: 0 }}
					transition={{ duration: 0.1, delay: 0.3, ease: theme.transitions.easings.motion.speedySwift }}
					isOnboarding={isOnboarding}
				>
					{shouldShowForm && shouldShowAddQuestionButton && fields.length < 10 && (
						<AddQuestionButton compact type="button" kind="primary" onClick={handleAddQuestion} icon={<PlusIcon />}>
							Add question
						</AddQuestionButton>
					)}
				</CardFooter>
			</>
		)
	},
)

QuestionsForm.displayName = 'QuestionsForm'

export default withDefaulValues(QuestionsForm)
